import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/layout/PageHeader';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { AlertTriangle, CheckCircle, Bell, RefreshCw, TrendingDown, Shield } from 'lucide-react';

const ProgressBar = ({ value, threshold, color = 'bg-blue-500' }) => {
  const isBelow = value < threshold;
  return (
    <div className="relative w-full">
      <div className="w-full bg-slate-200 rounded-full h-3">
        <div 
          className={`h-3 rounded-full transition-all duration-500 ${isBelow ? 'bg-red-500' : color}`} 
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
      {/* Threshold marker */}
      <div 
        className="absolute top-0 w-0.5 h-3 bg-amber-500"
        style={{ left: `${threshold}%` }}
        title={`Threshold: ${threshold}%`}
      />
    </div>
  );
};

export default function ComplianceDriftPage() {
  const [driftStatus, setDriftStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    fetchDriftStatus();
  }, []);

  const fetchDriftStatus = async () => {
    try {
      const response = await api.get('/compliance/drift-status');
      setDriftStatus(response.data);
    } catch (error) {
      toast.error('Failed to load drift status');
    } finally {
      setLoading(false);
    }
  };

  const checkAndAlert = async () => {
    setChecking(true);
    try {
      const response = await api.post('/compliance/check-drift');
      if (response.data.drift_detected) {
        toast.warning(`Compliance drift detected! ${response.data.total_alerts} alerts sent.`);
      } else {
        toast.success('No compliance drift detected. All frameworks above threshold.');
      }
      fetchDriftStatus();
    } catch (error) {
      toast.error('Failed to check compliance drift');
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  const alertCount = driftStatus?.frameworks_below_threshold || 0;

  return (
    <DashboardLayout>
      <PageHeader
        title="Compliance Drift Detection"
        subtitle="Monitor and alert when compliance drops below threshold"
        icon={<TrendingDown size={28} />}
        actions={
          <Button
            onClick={checkAndAlert}
            disabled={checking}
            data-testid="check-drift-btn"
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
          >
            {checking ? (
              <>
                <RefreshCw size={18} className="mr-2 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <Bell size={18} className="mr-2" />
                Check & Send Alerts
              </>
            )}
          </Button>
        }
      />

      {/* Alert Summary */}
      <div className={`rounded-sm p-6 mb-8 ${alertCount > 0 ? 'bg-red-50 border border-red-200' : 'bg-emerald-50 border border-emerald-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {alertCount > 0 ? (
              <AlertTriangle size={32} className="text-red-600" />
            ) : (
              <CheckCircle size={32} className="text-emerald-600" />
            )}
            <div>
              <h3 className={`font-semibold text-xl ${alertCount > 0 ? 'text-red-900' : 'text-emerald-900'}`}>
                {alertCount > 0 
                  ? `${alertCount} Framework${alertCount > 1 ? 's' : ''} Below Threshold`
                  : 'All Frameworks Above Threshold'}
              </h3>
              <p className={`text-sm ${alertCount > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                Threshold: {driftStatus?.threshold}% | Alerts: {driftStatus?.alerts_enabled ? 'Enabled' : 'Disabled'}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold text-slate-900">
              {driftStatus?.total_frameworks}
            </div>
            <div className="text-sm text-slate-500">Total Frameworks</div>
          </div>
        </div>
      </div>

      {/* Active Alerts */}
      {driftStatus?.alerts?.length > 0 && (
        <div className="mb-8">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <AlertTriangle size={20} className="text-red-500" />
            Active Drift Alerts
          </h3>
          <div className="space-y-3">
            {driftStatus.alerts.map((alert, idx) => (
              <div 
                key={idx} 
                className={`p-4 rounded-sm border ${
                  alert.alert_type === 'critical' 
                    ? 'bg-red-50 border-red-200' 
                    : 'bg-amber-50 border-amber-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase ${
                      alert.alert_type === 'critical' 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {alert.alert_type}
                    </span>
                    <span className="font-semibold">{alert.framework_name}</span>
                  </div>
                  <span className="text-sm text-slate-500">
                    {new Date(alert.timestamp).toLocaleString()}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Current:</span>{' '}
                    <span className="font-medium text-red-600">{alert.current_compliance}%</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Threshold:</span>{' '}
                    <span className="font-medium">{alert.threshold}%</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Drift:</span>{' '}
                    <span className="font-medium text-red-600">-{alert.drift_amount}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Framework Status Grid */}
      <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
        <Shield size={20} />
        Framework Compliance Status
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {driftStatus?.framework_status?.map((fw, idx) => (
          <div 
            key={idx} 
            className={`bg-white border rounded-sm p-6 ${
              fw.is_below_threshold ? 'border-red-200' : 'border-slate-200'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {fw.is_below_threshold ? (
                  <AlertTriangle size={20} className="text-red-500" />
                ) : (
                  <CheckCircle size={20} className="text-emerald-500" />
                )}
                <h4 className="font-semibold">{fw.framework_name}</h4>
              </div>
              <div className={`text-2xl font-bold ${
                fw.is_below_threshold ? 'text-red-600' : 'text-emerald-600'
              }`}>
                {fw.compliance_percentage}%
              </div>
            </div>
            
            <ProgressBar 
              value={fw.compliance_percentage} 
              threshold={fw.threshold}
              color={fw.is_below_threshold ? 'bg-red-500' : 'bg-emerald-500'}
            />
            
            <div className="mt-4 grid grid-cols-3 gap-2 text-center text-sm">
              <div className="bg-slate-50 rounded p-2">
                <div className="font-semibold">{fw.total_controls}</div>
                <div className="text-xs text-slate-500">Total</div>
              </div>
              <div className="bg-emerald-50 rounded p-2">
                <div className="font-semibold text-emerald-600">{fw.compliant_controls}</div>
                <div className="text-xs text-emerald-600">Compliant</div>
              </div>
              <div className="bg-amber-50 rounded p-2">
                <div className="font-semibold text-amber-600">{fw.total_controls - fw.compliant_controls}</div>
                <div className="text-xs text-amber-600">Remaining</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Configuration Info */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-sm p-6">
        <h3 className="font-semibold text-blue-900 mb-2">Configuration</h3>
        <div className="text-sm text-blue-700 space-y-1">
          <p><strong>Threshold:</strong> {driftStatus?.threshold}% (configurable via COMPLIANCE_DRIFT_THRESHOLD env var)</p>
          <p><strong>Alerts:</strong> {driftStatus?.alerts_enabled ? 'Enabled' : 'Disabled'} (DRIFT_ALERT_ENABLED env var)</p>
          <p><strong>Channels:</strong> Alerts are sent to Teams and Slack when webhooks are configured</p>
        </div>
      </div>
    </DashboardLayout>
  );
}
